import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Cookie, FileText } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LegalPage: React.FC = () => {
  const { t } = useLanguage();

  const sections = [
    {
      id: 'legal',
      icon: FileText,
      title: 'Mentions légales',
      content: `
        <h3>Informations générales</h3>
        <p><strong>Raison sociale :</strong> ${t('common.company.name')} S.à r.l.</p>
        <p><strong>Adresse :</strong> ${t('common.company.address')}, ${t('common.company.postalCode')} ${t('common.company.city')}, ${t('common.company.country')}</p>
        <p><strong>Téléphone :</strong> ${t('common.company.phone')}</p>
        <p><strong>Email :</strong> ${t('common.company.email')}</p>
        <p><strong>Création de l'entreprise :</strong> 22.06.2010</p>
        <p><strong>Gérant :</strong> Ramos Catarino Luis Alberto</p>
        
        <h3>Activité</h3>
        <p>Services de pneumatiques et projets automobiles sur-mesure, préparation circuit, entretien de véhicules classiques et de luxe.</p>
        
        <h3>Hébergement</h3>
        <p>Ce site web est hébergé par des services cloud sécurisés conformes aux standards européens de protection des données.</p>
        
        <h3>Propriété intellectuelle</h3>
        <p>L'ensemble des contenus présents sur ce site (textes, images, vidéos, logos) sont protégés par le droit d'auteur. Toute reproduction, même partielle, est interdite sans autorisation préalable.</p>
        
        <h3>Responsabilité</h3>
        <p>Les informations présentes sur ce site sont données à titre indicatif. Autocat ne saurait être tenu responsable des éventuelles erreurs ou omissions.</p>
      `
    },
    {
      id: 'privacy',
      icon: Shield,
      title: 'Politique de confidentialité',
      content: `
        <h3>Collecte des données</h3>
        <p>Nous collectons uniquement les données nécessaires à la fourniture de nos services :</p>
        <ul>
          <li>Données de contact (nom, email, téléphone)</li>
          <li>Informations sur votre véhicule pour nos prestations</li>
          <li>Photos que vous nous transmettez volontairement</li>
          <li>Données de navigation anonymisées pour améliorer notre site</li>
        </ul>
        
        <h3>Utilisation des données</h3>
        <p>Vos données sont utilisées exclusivement pour :</p>
        <ul>
          <li>Répondre à vos demandes et fournir nos services</li>
          <li>Vous recontacter concernant vos projets</li>
          <li>Améliorer la qualité de nos prestations</li>
          <li>Respecter nos obligations légales</li>
        </ul>
        
        <h3>Conservation</h3>
        <p>Nous conservons vos données personnelles uniquement le temps nécessaire à la fourniture de nos services, conformément à la législation en vigueur.</p>
        
        <h3>Vos droits</h3>
        <p>Conformément au RGPD, vous disposez des droits suivants :</p>
        <ul>
          <li>Droit d'accès et de rectification</li>
          <li>Droit à l'effacement</li>
          <li>Droit à la portabilité</li>
          <li>Droit d'opposition</li>
        </ul>
        
        <p>Pour exercer ces droits, contactez-nous à ${t('common.company.email')}</p>
        
        <h3>Sécurité</h3>
        <p>Nous mettons en œuvre toutes les mesures techniques et organisationnelles nécessaires pour protéger vos données contre tout accès non autorisé, altération, divulgation ou destruction.</p>
      `
    },
    {
      id: 'cookies',
      icon: Cookie,
      title: 'Politique des cookies',
      content: `
        <h3>Qu'est-ce qu'un cookie ?</h3>
        <p>Un cookie est un petit fichier texte déposé sur votre ordinateur lors de la visite d'un site web. Il permet de mémoriser des informations sur votre navigation.</p>
        
        <h3>Types de cookies utilisés</h3>
        <p><strong>Cookies strictement nécessaires :</strong> Ces cookies sont essentiels au fonctionnement du site (sélection de langue, préférences).</p>
        <p><strong>Cookies analytiques :</strong> Ils nous aident à comprendre comment vous utilisez notre site pour l'améliorer (Google Analytics, anonymisé).</p>
        <p><strong>Cookies de performance :</strong> Ils améliorent la vitesse et les performances du site.</p>
        
        <h3>Gestion des cookies</h3>
        <p>Vous pouvez à tout moment :</p>
        <ul>
          <li>Accepter ou refuser les cookies via notre bandeau</li>
          <li>Modifier vos préférences dans les paramètres de votre navigateur</li>
          <li>Supprimer les cookies déjà stockés</li>
        </ul>
        
        <h3>Cookies tiers</h3>
        <p>Nous utilisons Google Analytics pour mesurer l'audience de notre site. Ces services peuvent déposer des cookies soumis à leurs propres politiques de confidentialité.</p>
        
        <h3>Durée de conservation</h3>
        <p>Les cookies ont une durée de vie limitée et sont automatiquement supprimés après expiration :</p>
        <ul>
          <li>Cookies de session : supprimés à la fermeture du navigateur</li>
          <li>Cookies persistants : durée maximale de 13 mois</li>
        </ul>
        
        <p>Pour plus d'informations, consultez <a href="https://www.cnil.fr/fr/cookies-traceurs-que-dit-la-loi" target="_blank" rel="noopener noreferrer">la page de la CNIL sur les cookies</a>.</p>
      `
    }
  ];

  return (
    <div className="pt-24 min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Informations légales
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              Mentions légales, politique de confidentialité et gestion des cookies
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto mt-8 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => {
              const IconComponent = section.icon;
              
              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="bg-gradient-to-r from-red-600 to-red-700 p-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold text-white">
                        {section.title}
                      </h2>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    <div 
                      className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-600 prose-p:leading-relaxed prose-ul:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-900 prose-a:text-red-600 prose-a:hover:text-red-700"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Contact for Legal Matters */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 text-center p-8 bg-gradient-to-br from-gray-900 to-black rounded-2xl text-white"
          >
            <h3 className="text-2xl font-bold mb-4">
              Questions sur vos données ?
            </h3>
            <p className="text-gray-300 mb-6 text-lg">
              Pour toute question concernant le traitement de vos données personnelles ou l'exercice de vos droits.
            </p>
            <motion.a
              href={`mailto:${t('common.company.email')}`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-3 px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Eye className="w-5 h-5" />
              <span>Contactez-nous</span>
            </motion.a>
          </motion.div>

          {/* Last Updated */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-8 text-gray-500 text-sm"
          >
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;